import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Base from '../Base';
import Title from '../../components/common/Title';
import { useTranslation } from 'react-i18next';
import { fetchUserInfo } from '../../utils';
import Card from '../../components/elements/Card';
import Image from '../../components/images/Image';
import Icon from '../../components/common/Icon';
import Back from '../../components/common/Back';
import NotFound from '../../components/common/NotFound';
import Button from '../../components/elements/Button';

const Survey = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchUserInfo('/survey', setSurvey, setLoading, setError, []);
  }, []);


  // Loading / error / not found states
  if (loading) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to="/home" />
          <Title>
            <Icon name="survey-xmark" className="me-2" style={{ fontSize: '24px' }} theme="primary" />
            {t('surveys')}
          </Title>
        </Card>
        <NotFound translationKey="loading" loading={true} />
      </Base>
    );
  }

  if (error || !survey) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to="/event-surveys" />
          <Title>
            <Icon name="survey-xmark" className="me-2" style={{ fontSize: '24px' }} theme="primary" />
            {t('surveys')}
          </Title>
        </Card>
        <NotFound translationKey="errorLoading" />
      </Base>
    );
  }

  // Date logic
  const now = new Date();
  const start = survey.start_date ? new Date(survey.start_date) : null;
  const end = survey.end_date ? new Date(survey.end_date) : null;

  let status = 'active';
  if (start && now < start) {
    status = 'not_started';
  } else if (end && now > end) {
    status = 'expired';
  }

  const isActive = status === 'active';

  const alreadySubmitted = survey.already_submitted === true;
  const canStart = isActive && !alreadySubmitted;


  const formattedStart = start ? start.toLocaleDateString() : '—';
  const formattedEnd = end ? end.toLocaleDateString() : '—';


  return (
    <Base activeNav="home">
      {/* Main content */}
      <Card className="mb-3">
        <Back to="/event-surveys" />
        <div className="d-flex flex-column align-items-center">
          {/* Image */}
          <div className="mb-5 mt-2">
            <Image
              size="10rem"
              className=""
              url={
                survey.picture_url
                  ? survey.picture_url
                  : require('./../../assets/images/fallback/survey.png')
              }
            />
          </div>

          {/* Info */}
          <div className="">
            <h4 className="mb-2">{survey.name}</h4>

            <div className="mb-2 text-muted" style={{ fontSize: '14px' }}>
              <Icon name="calendar" className="me-1" />
              {formattedStart} &rarr; {formattedEnd}
            </div>

            <div className="mb-2" style={{ fontSize: '14px' }}>
              {survey.language && (
                <span className="me-3">
                  <Icon name="globe" className="me-1" />
                  {survey.language.toUpperCase()}
                </span>
              )}
              {survey.allow_anonymous_answers && (
                <span className="badge bg-secondary">
                  <Icon name="incognito" className="me-1" />
                  {t('anonymousSurvey') || 'Anonymous survey'}
                </span>
              )}
            </div>

            {survey.completion_points > 0 && (
              <div className="mb-2">
                <span className="badge bg-primary">
                  <Icon name="star" className="me-1" />
                  {t('surveyEarnPoints', { points: survey.completion_points })}
                </span>
              </div>
            )}

            <div className="mb-3">
              {status === 'active' && (
                <span className="badge bg-success me-2">
                  {t('open') || 'Open'}
                </span>
              )}
              {status === 'not_started' && (
                <span className="badge bg-warning text-dark me-2">
                  {t('notStarted') || 'Not open yet'}
                </span>
              )}
              {status === 'expired' && (
                <span className="badge bg-danger me-2">
                  {t('closed') || 'Closed'}
                </span>
              )}

              {alreadySubmitted && (
                <span className="badge bg-info text-dark">
                  {t('surveyAlreadySubmitted') || 'You have already completed this survey.'}
                </span>
              )}

              {!alreadySubmitted && survey.team_submitted && (
                <span className="badge bg-secondary">
                  <Icon name="users" className="me-1" />
                  {t('quizTeamCompleted')}
                </span>
              )}
            </div>


            <Button
              to="/questions"
              disabled={!canStart}
            >
              {alreadySubmitted
                ? (t('surveyAlreadySubmittedCta') || 'Survey already completed')
                : (isActive
                    ? (t('startSurvey') || 'Start survey')
                    : (status === 'not_started'
                        ? t('surveyNotAvailableYet') || 'Survey not available yet'
                        : t('surveyAlreadyClosed') || 'Survey is closed'))}
            </Button>

          </div>
        </div>

      </Card>

      {/* Optional: small card with privacy / info */}
      
        <div style={{ fontSize: '13px' }} className="text-muted mx-3">
          <Icon name="shield-check" className="me-1" />
          {survey.allow_anonymous_answers
            ? (t('surveyPrivacyAnonymous') ||
               'Your answers will be treated confidentially and cannot be linked back to you personally.')
            : (t('surveyPrivacyStandard') ||
               'Your answers will be treated confidentially and only used in aggregated form.')}
        </div>
      
      {/* Extra helptext when NOT anonymous */}
      {!survey.allow_anonymous_answers && (
        <div style={{ fontSize: '13px' }} className="text-muted mx-3 mt-2">
          <Icon name="info" className="me-1" />
          {t('surveyFirstSubmitPoints') ||
            'Only the first person in your team who submits will receive the points.'}
        </div>
      )}

    </Base>
  );
};

export default Survey;
